import React, { useState } from 'react';
import { Loader2, Pencil, Printer } from 'lucide-react';
import {
  Button,
  Checkbox,
  ConfirmationDialog,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from '@roger/ui-components';
import EOEClaimsWorksheet from '../EOEClaimsWorksheet';
import {
  client,
  downloadBillingCycleUb04Pdf,
  getActiveBillingCycles,
  setBillingCycleStatusReadyToSend,
  setBillingCycleStatusVoid,
} from '../../../services/api';
import { useToast } from '../../../hooks/useToastContext';
import type {
  ActiveBillingCycleItem,
  EpisodicClaimStatus,
} from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { useBillingCycles } from '../../../hooks/useBillingCycles';

interface EOEPendingApprovalEntryProps {
  entry: ActiveBillingCycleItem;
  index?: number;
  isSelected: boolean;
  onToggle: (id: string) => void;
  onEdit: (entry: ActiveBillingCycleItem) => void;
  onDownload: (billingCycleId: string) => void;
  isDownloading: boolean;
}

interface EOEPendingApprovalTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOEPendingApprovalEntry: React.FC<EOEPendingApprovalEntryProps> = ({
  entry,
  index,
  isSelected,
  onToggle,
  onEdit,
  onDownload,
  isDownloading,
}) => {
  const startDate = formatDateToMMDDYYYY(entry.billing_period_start);
  const endDate = formatDateToMMDDYYYY(entry.billing_period_end);
  const isAlternate = index !== undefined && index % 2 === 1;

  return (
    <TableRow
      className={
        isAlternate
          ? 'bg-muted hover:bg-muted'
          : 'bg-background hover:bg-background'
      }
    >
      <TableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle(entry.billing_cycle_id)}
        />
      </TableCell>
      <TableCell>{entry.patient_name}</TableCell>
      <TableCell>{entry.patient_mrn}</TableCell>
      <TableCell>{entry.branch}</TableCell>
      <TableCell>{entry.insurance_name}</TableCell>
      <TableCell>
        <span>{startDate} -</span>
        <br />
        <span>{endDate}</span>
      </TableCell>
      <TableCell>{entry.tob}</TableCell>
      <TableCell>{entry.total_charges}</TableCell>
      <TableCell>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          onClick={() => onEdit(entry)}
          className='text-muted-foreground hover:text-foreground'
          aria-label='Edit billing cycle'
        >
          <Pencil className='h-4 w-4' />
        </Button>
      </TableCell>
      <TableCell>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          onClick={() => onDownload(entry.billing_cycle_id)}
          disabled={isDownloading}
          className='text-muted-foreground hover:text-foreground'
          aria-label='Download claim PDF'
        >
          {isDownloading ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <Printer className='h-4 w-4' />
          )}
        </Button>
      </TableCell>
    </TableRow>
  );
};

const EOEPendingApprovalTab: React.FC<EOEPendingApprovalTabProps> = ({
  onCountsInvalidated,
}) => {
  const [editEntry, setEditEntry] = useState<ActiveBillingCycleItem | null>(
    null
  );
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const { error: showError } = useToast();

  const getErrorStatus = (error: unknown): number | undefined => {
    const maybeAxiosError = error as {
      response?: { status?: number };
      status?: number;
    };
    return maybeAxiosError.response?.status ?? maybeAxiosError.status;
  };

  const extractErrorDetail = async (
    error: unknown
  ): Promise<string | undefined> => {
    const maybeAxiosError = error as { response?: { data?: unknown } };
    const data = maybeAxiosError.response?.data ?? error;

    if (typeof data === 'string') return data;

    if (
      typeof data === 'object' &&
      data !== null &&
      'detail' in data &&
      typeof (data as { detail?: unknown }).detail === 'string'
    ) {
      return (data as { detail: string }).detail;
    }

    // When `responseType: 'blob'` is used, JSON errors may come back as a Blob.
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        try {
          const parsed = JSON.parse(text) as unknown;
          if (
            typeof parsed === 'object' &&
            parsed !== null &&
            'detail' in parsed &&
            typeof (parsed as { detail?: unknown }).detail === 'string'
          ) {
            return (parsed as { detail: string }).detail;
          }
        } catch {
          // Not JSON, fall back to raw text.
        }
        return text.trim() || undefined;
      } catch {
        return undefined;
      }
    }

    return undefined;
  };

  const {
    data,
    isLoading,
    isSubmitting,
    selectedIds,
    handleToggleId,
    handleToggleAll,
    mutateSelected,
  } = useBillingCycles<
    ActiveBillingCycleItem,
    { statuses: EpisodicClaimStatus[] }
  >({
    fetchFn: getActiveBillingCycles,
    fetchPayload: { statuses: ['PENDING_APPROVAL'] },
    errorMessage: 'Could not fetch data for the "Pending Approval" tab.',
    getId: item => item.billing_cycle_id,
    getName: item => item.patient_name,
  });

  const handleDownload = async (billingCycleId: string) => {
    setDownloadingId(billingCycleId);
    try {
      const resp = await downloadBillingCycleUb04Pdf({
        client,
        path: { billing_cycle_id: billingCycleId },
        responseType: 'blob',
      });
      if (resp.error) {
        console.error('Failed to download UB-04 PDF:', resp.error);
        const status = getErrorStatus(resp);
        const detail = await extractErrorDetail(resp);
        showError(
          'Failed to generate claim',
          status === 500
            ? 'Unexpected error occurred. Please try again or contact support'
            : (detail ??
                'Unexpected error occurred. Please try again or contact support')
        );
        return;
      }
      if (!resp.data) {
        console.error('Failed to download UB-04 PDF: no data returned');
        showError(
          'Failed to generate claim',
          'Unexpected error occurred. Please try again or contact support'
        );
        return;
      }
      const blob =
        resp.data instanceof Blob
          ? resp.data
          : new Blob([resp.data as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ub04_billing_cycle_${billingCycleId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download UB-04 PDF:', error);
      const status = getErrorStatus(error);
      const detail = await extractErrorDetail(error);
      showError(
        'Failed to generate claim',
        status === 500
          ? 'Unexpected error occurred. Please try again or contact support'
          : (detail ??
              'Unexpected error occurred. Please try again or contact support')
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const handleConfirmApprove = () => {
    setApproveConfirmOpen(false);
    void mutateSelected(
      id =>
        setBillingCycleStatusReadyToSend({
          client,
          path: { billing_cycle_id: id },
        }),
      'Some billing cycles failed to approve',
      'The following billing cycles could not be updated',
      onCountsInvalidated
    );
  };

  const handleVoid = () => {
    void mutateSelected(
      id =>
        setBillingCycleStatusVoid({
          client,
          path: { billing_cycle_id: id },
        }),
      'Some billing cycles failed to void',
      'The following billing cycles could not be updated',
      onCountsInvalidated
    );
  };

  if (editEntry !== null) {
    return (
      <EOEClaimsWorksheet
        billingCycle={editEntry}
        onClose={() => setEditEntry(null)}
      />
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      {isLoading ? (
        <p className='text-muted-foreground text-center py-10'>Loading...</p>
      ) : data.length === 0 ? (
        <p className='text-muted-foreground text-center py-10'>
          There are currently no records to display
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Checkbox
                  checked={data.length > 0 && selectedIds.size === data.length}
                  onCheckedChange={handleToggleAll}
                />
              </TableHead>
              <TableHead>Patient Name</TableHead>
              <TableHead>MRN</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Insurance</TableHead>
              <TableHead>Billing Period</TableHead>
              <TableHead>TOB</TableHead>
              <TableHead>Total Charges</TableHead>
              <TableHead>Edit</TableHead>
              <TableHead>Download Claim</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((entry, index) => (
              <EOEPendingApprovalEntry
                key={entry.billing_cycle_id}
                entry={entry}
                index={index}
                isSelected={selectedIds.has(entry.billing_cycle_id)}
                onToggle={handleToggleId}
                onEdit={setEditEntry}
                onDownload={id => void handleDownload(id)}
                isDownloading={downloadingId === entry.billing_cycle_id}
              />
            ))}
          </TableBody>
        </Table>
      )}
      <ConfirmationDialog
        open={approveConfirmOpen}
        title='Approve billing cycles?'
        message='This action cannot be undone, please make sure all information is correct'
        confirmLabel='Approve'
        onAccept={handleConfirmApprove}
        onCancel={() => setApproveConfirmOpen(false)}
      />
      <div className='flex justify-end gap-2'>
        <Button
          variant='purple'
          disabled={selectedIds.size === 0 || isSubmitting || isLoading}
          onClick={() => setApproveConfirmOpen(true)}
        >
          {isSubmitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
          Approve
        </Button>
        <Button
          variant='destructive'
          disabled={selectedIds.size === 0 || isSubmitting || isLoading}
          onClick={handleVoid}
        >
          {isSubmitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
          Void
        </Button>
      </div>
    </div>
  );
};

export default EOEPendingApprovalTab;
